/**
 * LineChart — Reusable ECharts line chart wrapper
 */

import { lazy, Suspense, useMemo } from "react";
import { CHART_COLORS, getChartTheme, useThemeVersion } from "./chart-theme";

const ReactECharts = lazy(() => import("echarts-for-react"));

interface LineChartSeries {
  name: string;
  data: (number | null)[];
  color?: string;
  smooth?: boolean;
  areaStyle?: boolean;
}

interface LineChartProps {
  categories: string[];
  series: LineChartSeries[];
  height?: number;
  yAxisName?: string;
  invertYAxis?: boolean;
  showLegend?: boolean;
}

export function LineChart({
  categories,
  series,
  height = 250,
  yAxisName,
  invertYAxis = false,
  showLegend = true,
}: LineChartProps) {
  const themeVersion = useThemeVersion();
  const option = useMemo(() => {
    const theme = getChartTheme();

    return {
      backgroundColor: "transparent",
      tooltip: {
        trigger: "axis",
        backgroundColor: "rgba(0,0,0,0.8)",
        borderColor: "transparent",
        textStyle: { color: "#fff", fontSize: 12 },
      },
      legend:
        showLegend && series.length > 1
          ? {
              data: series.map((s) => s.name),
              textStyle: { color: theme.mutedForeground, fontSize: 11 },
              bottom: 0,
            }
          : undefined,
      grid: {
        left: 40,
        right: 16,
        top: 16,
        bottom: showLegend && series.length > 1 ? 36 : 16,
        containLabel: false,
      },
      xAxis: {
        type: "category" as const,
        data: categories,
        axisLabel: {
          color: theme.mutedForeground,
          fontSize: 10,
          rotate: categories.length > 12 ? 45 : 0,
        },
        axisLine: { lineStyle: { color: theme.border } },
        axisTick: { show: false },
      },
      yAxis: {
        type: "value" as const,
        name: yAxisName,
        nameTextStyle: { color: theme.mutedForeground, fontSize: 10 },
        inverse: invertYAxis,
        axisLabel: { color: theme.mutedForeground, fontSize: 10 },
        splitLine: { lineStyle: { color: theme.border, opacity: 0.3 } },
      },
      series: series.map((s, i) => ({
        name: s.name,
        type: "line" as const,
        data: s.data,
        smooth: s.smooth ?? true,
        color: s.color ?? CHART_COLORS[i % CHART_COLORS.length],
        lineStyle: { width: 2 },
        symbolSize: 4,
        areaStyle: s.areaStyle
          ? {
              opacity: 0.1,
            }
          : undefined,
        animationDuration: 800,
        animationDelay: i * 200,
      })),
    };
  }, [
    categories,
    series,
    height,
    yAxisName,
    invertYAxis,
    showLegend,
    themeVersion,
  ]);

  return (
    <Suspense fallback={<div style={{ height: `${height}px` }} />}>
      <ReactECharts
        option={option}
        style={{ height: `${height}px`, width: "100%" }}
        opts={{ renderer: "canvas" }}
      />
    </Suspense>
  );
}
